import { useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import classNames from 'classnames'
import debounce from 'lodash/debounce'
import { CameraIcon } from '@/atoms/Icons/CameraIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { MovieSpecialIcon } from '@/atoms/Icons/MovieSpecialIcon'
import { PhotonInput } from '@/atoms/PhotonInput'
import { CaptionSM, ParagraphSM, TitleXS } from '@/atoms/Text'
import {
  UpdateApplicationAssetAliasMutation,
  UpdateApplicationAssetAliasMutationVariables,
} from '@/types/codegen-federation'
import { ReactFCC } from '@/types/react'
import { useTranslate } from '@/utils/translate/translate-client'
import { useTorchApplication } from '../../components/TorchApplicationContext'
import { DELETE_UPLOADED_FILE, UPDATE_TORCH_APPLICATION_ALIAS } from '../../queries'
import { formatSize } from './uploadHelpers'

type UploadFile = {
  type: string
  fileName: string
  size: number
  alias?: string | null
}

interface FileBoxProps {
  file: UploadFile
  loadFiles: () => void
  disabled?: boolean
}

export const FileBox: ReactFCC<FileBoxProps> = ({ file, loadFiles, disabled }) => {
  const { t } = useTranslate('guild-create')
  const { id } = useTorchApplication()
  const [alias, setAlias] = useState(file?.alias || '')

  const isVideo = !!file?.type?.includes('video')
  const isImage = !!file?.type?.includes('image')

  const [deleteFile] = useMutation(DELETE_UPLOADED_FILE, {
    variables: { input: { id, fileName: file.fileName } },
    onCompleted: () => {
      loadFiles()
    },
    errorPolicy: 'all',
  })

  const [updateAlias] = useMutation<UpdateApplicationAssetAliasMutation, UpdateApplicationAssetAliasMutationVariables>(
    UPDATE_TORCH_APPLICATION_ALIAS,
  )

  const handleUpdateAlias = useMemo(
    () =>
      debounce((alias: string) => {
        if (id)
          updateAlias({
            variables: {
              input: {
                applicationId: id,
                fileName: file.fileName,
                alias,
              },
            },
          })
      }, 500),
    [file.fileName, id, updateAlias],
  )

  const handleDelete = () => {
    if (
      confirm(
        t('areYouSureYouWantToDeleteThisFile', 'Are you sure you want to delete this file? This cannot be undone.'),
      )
    ) {
      deleteFile()
    }
  }

  const fileBytes = file.size * 1024
  return (
    <div className={classNames('flex flex-col w-full justify-start items-center gap-2')}>
      <div className="flex w-full flex-1 select-none flex-row items-center gap-4  rounded-lg border border-core-gray-300 p-4">
        <div className="flex h-20 w-20 items-center justify-center rounded-md border border-info-300 bg-info-100">
          {isImage && <CameraIcon size={48} color="info-500" />}
          {isVideo && <MovieSpecialIcon size={48} color="info-500" />}
        </div>
        <div className="flex-1">
          <TitleXS weight="bold">{file.fileName}</TitleXS>
          <ParagraphSM color="core-gray-600">{formatSize(fileBytes)}</ParagraphSM>
        </div>
        {!disabled && (
          <div className="">
            <button className="rounded-full bg-core-gray-100 p-2" onClick={handleDelete}>
              <CloseIcon size={24} color="core-gray-950" />
            </button>
          </div>
        )}
      </div>
      {isVideo && (
        <div className="flex w-full flex-col items-start justify-center gap-2">
          <PhotonInput
            containerClassName="w-full"
            label={t('versionIdentifierOptions', 'Version Identifier (Optional)')}
            value={alias}
            onChange={(e) => {
              setAlias(e.target.value)
              handleUpdateAlias(e.target.value)
            }}
          />
          <CaptionSM color="core-gray-600">
            {t(
              'thisIsForYourReferenceOnlyNotVisibleToGuildMembers',
              'This is for your reference only. Not visible to Guild members.',
            )}
          </CaptionSM>
        </div>
      )}
    </div>
  )
}
